import React, { useState, useMemo, useCallback, useId } from 'react';
import { ArrowLeftRight, ArrowRight } from 'lucide-react';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Pill } from '../ui';

// ── Types ──

type FieldState = 'only_target' | 'only_source' | 'same' | 'conflict';

interface MergeField {
  key: string;
  label: string;
  targetValue: string;
  sourceValue: string;
  state: FieldState;
  chosen: 'target' | 'source';
}

export interface MergeFieldDef {
  key: string;
  label: string;
}

export interface MergeFieldGroup {
  title: string;
  icon: React.ForwardRefExoticComponent<any>;
  fields: MergeFieldDef[];
}

interface MergeResolutionSheetProps {
  open: boolean;
  onClose: () => void;
  entityType: 'stakeholder' | 'organization';
  sourceData: Record<string, any>;
  targetData: Record<string, any>;
  sourceLabel: string;
  targetLabel: string;
  fieldGroups: MergeFieldGroup[];
  onSwap: () => void;
  onConfirmMerge: (resolvedFields: Record<string, any>) => Promise<void>;
  merging: boolean;
  transferSummary?: string;
}

// ── Helpers ──

function buildMergeFields(
  targetData: Record<string, any>,
  sourceData: Record<string, any>,
  fieldGroups: MergeFieldGroup[],
): MergeField[] {
  const fields: MergeField[] = [];

  for (const group of fieldGroups) {
    for (const { key, label } of group.fields) {
      const targetVal = (targetData[key] ?? '').toString().trim();
      const sourceVal = (sourceData[key] ?? '').toString().trim();

      let state: FieldState;
      if (targetVal && !sourceVal) {
        state = 'only_target';
      } else if (!targetVal && sourceVal) {
        state = 'only_source';
      } else if (targetVal === sourceVal) {
        state = 'same';
      } else {
        state = 'conflict';
      }

      fields.push({
        key,
        label,
        targetValue: targetVal,
        sourceValue: sourceVal,
        state,
        chosen: state === 'only_source' ? 'source' : 'target',
      });
    }
  }

  return fields;
}

// ── Component ──

export const MergeResolutionSheet: React.FC<MergeResolutionSheetProps> = ({
  open,
  onClose,
  entityType,
  sourceData,
  targetData,
  sourceLabel,
  targetLabel,
  fieldGroups,
  onSwap,
  onConfirmMerge,
  merging,
  transferSummary,
}) => {
  const [mergeFields, setMergeFields] = useState<MergeField[]>(() =>
    buildMergeFields(targetData, sourceData, fieldGroups),
  );

  // Rebuild merge fields when data changes (e.g., after swap)
  const prevTargetRef = React.useRef(targetData);
  const prevSourceRef = React.useRef(sourceData);
  if (prevTargetRef.current !== targetData || prevSourceRef.current !== sourceData) {
    prevTargetRef.current = targetData;
    prevSourceRef.current = sourceData;
    setMergeFields(buildMergeFields(targetData, sourceData, fieldGroups));
  }

  const handleFieldChoice = useCallback((fieldKey: string, chosen: 'target' | 'source') => {
    setMergeFields(prev =>
      prev.map(f => (f.key === fieldKey ? { ...f, chosen } : f)),
    );
  }, []);

  const handleConfirm = useCallback(async () => {
    const resolved: Record<string, any> = {};

    for (const field of mergeFields) {
      if (field.state === 'only_target' || field.state === 'same') {
        // Keep target — no update needed
        continue;
      }
      if (field.state === 'only_source') {
        // Add source data to target
        resolved[field.key] = field.sourceValue;
      }
      if (field.state === 'conflict') {
        if (field.chosen === 'source') {
          resolved[field.key] = field.sourceValue;
        }
        // If 'target' is chosen, no update needed (it's already on target)
      }
    }

    await onConfirmMerge(resolved);
  }, [mergeFields, onConfirmMerge]);

  const changeCount = useMemo(() => {
    return mergeFields.filter(
      f => f.state === 'only_source' || (f.state === 'conflict' && f.chosen === 'source'),
    ).length;
  }, [mergeFields]);

  const conflictCount = useMemo(() => {
    return mergeFields.filter(f => f.state === 'conflict').length;
  }, [mergeFields]);

  const title = `Merge ${entityType === 'stakeholder' ? 'Stakeholders' : 'Organizations'}`;
  const titleId = useId();
  // Closing mid-merge would hide the result; Cancel is disabled then too
  const close = () => {
    if (!merging) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      labelledBy={titleId}
      handle
      className="max-h-[92dvh] sm:max-h-[84vh] sm:max-w-lg"
    >
      <DialogHeader id={titleId} title={title} onClose={close} className="max-sm:pt-1" />

      <DialogBody className="space-y-5 pt-3">
        {/* Direction */}
        <div className="rounded-2xl bg-neutral-50 p-3 ring-1 ring-inset ring-neutral-200/70">
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 text-[13px]">
              <span className="min-w-0 truncate text-neutral-500 line-through decoration-neutral-300" title={sourceLabel}>
                {sourceLabel}
              </span>
              <ArrowRight className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} aria-label="into" />
              <span className="min-w-0 truncate text-ink" title={targetLabel}>
                {targetLabel}
              </span>
            </div>
            <Button
              size="xs"
              variant="secondary"
              onClick={onSwap}
              disabled={merging}
              title="Swap direction"
              aria-label="Swap direction"
              className="flex-shrink-0"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              Swap
            </Button>
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-neutral-400">
            {sourceLabel} will be removed.
            {transferSummary && ` ${transferSummary}`}
            {changeCount > 0 && ` ${changeCount} field${changeCount !== 1 ? 's' : ''} will be updated.`}
            {conflictCount > 0 && ` ${conflictCount} conflict${conflictCount !== 1 ? 's' : ''} to review.`}
          </p>
        </div>

        {/* Grouped fields */}
        {fieldGroups.map((group) => {
          const groupFields = mergeFields.filter(f =>
            group.fields.some(gf => gf.key === f.key),
          );
          const hasContent = groupFields.some(
            f => f.targetValue || f.sourceValue,
          );
          if (!hasContent) return null;

          const IconComp = group.icon;

          return (
            <section key={group.title}>
              <h3 className="mb-2 flex items-center gap-1.5 px-1 text-[12px] text-neutral-500">
                <IconComp className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} aria-hidden="true" />
                {group.title}
              </h3>
              <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
                {groupFields.map((field) => (
                  <MergeFieldRow
                    key={field.key}
                    field={field}
                    targetLabel={targetLabel}
                    sourceLabel={sourceLabel}
                    onChoice={handleFieldChoice}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" onClick={close} disabled={merging} className="h-11 sm:h-9">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} loading={merging} className="h-11 flex-1 sm:h-9 sm:flex-none">
          {merging ? 'Merging…' : `Merge${changeCount > 0 ? ` (${changeCount})` : ''}`}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

// ── Individual Field Row ──

interface MergeFieldRowProps {
  field: MergeField;
  targetLabel: string;
  sourceLabel: string;
  onChoice: (fieldKey: string, chosen: 'target' | 'source') => void;
}

const MergeFieldRow: React.FC<MergeFieldRowProps> = ({ field, targetLabel, sourceLabel, onChoice }) => {
  if (!field.targetValue && !field.sourceValue) return null;

  const value =
    field.state === 'only_source' ? field.sourceValue : field.state === 'conflict' ? null : field.targetValue;

  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] text-neutral-500">{field.label}</span>
        <FieldStateBadge state={field.state} chosen={field.chosen} />
      </div>

      {value !== null && <p className="mt-0.5 break-words text-[13px] text-ink">{value}</p>}

      {field.state === 'conflict' && (
        <div className="mt-2 space-y-1.5" role="radiogroup" aria-label={field.label}>
          <MergeChoice
            name={`merge-${field.key}`}
            checked={field.chosen === 'target'}
            onSelect={() => onChoice(field.key, 'target')}
            caption={`Keep · ${targetLabel}`}
            value={field.targetValue}
          />
          <MergeChoice
            name={`merge-${field.key}`}
            checked={field.chosen === 'source'}
            onSelect={() => onChoice(field.key, 'source')}
            caption={`Use · ${sourceLabel}`}
            value={field.sourceValue}
          />
        </div>
      )}
    </div>
  );
};

const MergeChoice: React.FC<{
  name: string;
  checked: boolean;
  onSelect: () => void;
  caption: string;
  value: string;
}> = ({ name, checked, onSelect, caption, value }) => (
  <label
    className={`flex cursor-pointer items-start gap-2.5 rounded-xl px-3 py-2 ring-1 ring-inset transition-colors ${
      checked ? 'bg-primary-50/50 ring-primary-600/40' : 'ring-neutral-200 hover:bg-neutral-50'
    }`}
  >
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onSelect}
      className="mt-0.5 h-4 w-4 flex-shrink-0 border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-600/30 focus:ring-offset-0"
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[12px] text-neutral-400">{caption}</span>
      <span className="block text-[13px] text-ink [overflow-wrap:anywhere]">{value}</span>
    </span>
  </label>
);

// ── State Badge ──

const FieldStateBadge: React.FC<{ state: FieldState; chosen: 'target' | 'source' }> = ({
  state,
  chosen,
}) => {
  switch (state) {
    case 'same':
      return <Pill>Same</Pill>;
    case 'only_target':
      return <Pill>Kept</Pill>;
    case 'only_source':
      return <Pill tone="green">Added</Pill>;
    case 'conflict':
      return chosen === 'source' ? <Pill tone="blue">Updated</Pill> : <Pill tone="amber">Review</Pill>;
  }
};
